import { useState, useEffect } from "react";
import ActionButton from "../components/atoms/ActionButton";
import YearMonthNavigator from "../components/molecules/YearMonthNavigator";
import CollapsibleTh from "../components/molecules/CollapsibleTh";
import { fetchMonthlyReports } from "../services/dailyReportService";
import { getCurrentUser } from "../services/authService";
import useLockStatus from "../hooks/useLockStatus";

/**
 * 一覧テーブルの列定義
 * DB列名 と 表示ラベル の対応
 * クレジット・ギフト券・商品券・電子マネーは UI から削除済み（DBカラムは保持）
 */
const COLUMNS = [
  { key: "GenkinUriage",  label: "現金売上" },
  { key: "KakeUriage",    label: "掛売上" },
  { key: "SonotaUriage",  label: "その他売上" },
  { key: "SyohiZei",      label: "うち消費税" },
  { key: "SonotaKoujo",   label: "その他控除" },
  { key: "SoUriage",      label: "総売上" },
  { key: "JunUriage",     label: "純売上" },
  { key: "CustNo",        label: "客数" },
  { key: "GenkinAridaka", label: "入金機投入額" },
  // 現金相違額: DB列なし・フロント算出（現金売上 - 入金機投入額）
  { key: "_kabusoku",     label: "現金相違額", signed: true,
    getValue: (row) => row ? toNum(row.GenkinUriage) - toNum(row.GenkinAridaka) : null },
  { key: "InputPerson",   label: "担当者", isText: true },
];

// 全列を表示状態で初期化する
const buildInitialVisibility = () =>
  Object.fromEntries(COLUMNS.map(({ key }) => [key, true]));

// 日本時間の現在年月を YYYYMM 形式で返す
const getJapanYearMonth = () => {
  const jst = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" }),
  );
  return `${jst.getFullYear()}${String(jst.getMonth() + 1).padStart(2, "0")}`;
};

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

// null/undefined/NaN は 0 として数値変換する
const toNum = (v) => (v == null ? 0 : Number(v));

// null/undefined は空文字、数値はカンマ区切りで表示
const formatNumber = (v) => (v == null ? "" : Number(v).toLocaleString());

// 符号付きフォーマット: 正の値に "+" を付与する
const formatSigned = (v) => (v == null ? "" : (v > 0 ? "+" : "") + Number(v).toLocaleString());

// CSV 出力列の定義（折りたたみ状態に関係なく常に全列出力）
const CSV_COLUMNS = [
  { label: "日付",       getValue: (row, yyyymmdd) => formatDateDisplay(yyyymmdd) },
  { label: "店舗番号",   getValue: (row, yyyymmdd, user) => user?.shopNo ?? "" },
  { label: "レジ番号",   getValue: (row, yyyymmdd, user) => user?.regiNo ?? "" },
  { label: "現金売上",   getValue: (row) => row?.GenkinUriage ?? "" },
  { label: "掛売上",     getValue: (row) => row?.KakeUriage ?? "" },
  { label: "その他売上", getValue: (row) => row?.SonotaUriage ?? "" },
  { label: "うち消費税",     getValue: (row) => row?.SyohiZei ?? "" },
  { label: "その他控除", getValue: (row) => row?.SonotaKoujo ?? "" },
  { label: "総売上",       getValue: (row) => row?.SoUriage ?? "" },
  { label: "純売上",       getValue: (row) => row?.JunUriage ?? "" },
  { label: "客数",         getValue: (row) => row?.CustNo ?? "" },
  { label: "入金機投入額", getValue: (row) => row?.GenkinAridaka ?? "" },
  { label: "現金相違額",   getValue: (row) => row ? toNum(row.GenkinUriage) - toNum(row.GenkinAridaka) : "" },
  { label: "担当者",       getValue: (row) => row ? (row.UpdatePerson ?? row.InputPerson ?? "") : "" },
];

// 指定年月の全日付を YYYYMMDD 配列で返す
const getDaysInMonth = (yearMonth) => {
  const year = parseInt(yearMonth.slice(0, 4), 10);
  const month = parseInt(yearMonth.slice(4, 6), 10);
  const lastDay = new Date(year, month, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const d = String(i + 1).padStart(2, "0");
    return `${year}${String(month).padStart(2, "0")}${d}`;
  });
};

/**
 * MonthlyDataPage - 月毎データ一覧ページ
 *
 * @param {function} onNavigateToStore - 編集ボタン押下時に店舗入力ページへ遷移するコールバック
 *                                       App.js の navigateToStore(date) が渡される
 */
function MonthlyDataPage({ onNavigateToStore }) {
  const [yearMonth, setYearMonth] = useState(getJapanYearMonth);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleCols, setVisibleCols] = useState(buildInitialVisibility);
  const { isLocked } = useLockStatus();
  const user = getCurrentUser();

  // 表示月が変わるたびにデータを再取得する
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;

    setLoading(true);
    setReports([]);
    fetchMonthlyReports(user.shopNo, user.regiNo, yearMonth)
      .then((data) => setReports(data))
      .catch((err) => console.error("月次データ取得失敗:", err))
      .finally(() => setLoading(false));
  }, [yearMonth]);

  // 列の表示・非表示を切り替える
  const toggleCol = (key) => {
    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const days = getDaysInMonth(yearMonth);

  // 表示中の月データを CSV ファイルとしてダウンロードする
  const downloadCsv = () => {
    const user = getCurrentUser();
    const header = CSV_COLUMNS.map((c) => c.label).join(",");
    const dataRows = days.map((yyyymmdd) => {
      const row = reports.find((r) => r.YYYYMMDD === yyyymmdd) ?? null;
      return CSV_COLUMNS.map((c) => {
        const val = String(c.getValue(row, yyyymmdd, user));
        // カンマ・改行・ダブルクォートを含む値はダブルクォートで囲む
        return val.includes(",") || val.includes("\n") || val.includes('"')
          ? `"${val.replace(/"/g, '""')}"`
          : val;
      }).join(",");
    });

    // UTF-8 BOM を付けることで Excel が文字化けせずに開ける
    const bom = "﻿";
    const blob = new Blob([bom + [header, ...dataRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${yearMonth}_売上データ.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      {/* 月切り替えヘッダー */}
      <div className="flex items-center gap-4 mb-6">
        <YearMonthNavigator yearMonth={yearMonth} onChange={setYearMonth} />
        <ActionButton
          label="CSVダウンロード"
          onClick={downloadCsv}
          disabled={loading || reports.length === 0}
          paddingClass="px-4 py-2"
          textSizeClass="text-base"
        />
      </div>

      {loading ? (
        <p className="text-gray-400 text-base">読み込み中...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="border-collapse text-base">
            <thead>
              <tr>
                {/* 操作列: 左端に固定 */}
                <th className="border border-gray-400 w-16 px-2 py-2 font-normal whitespace-nowrap bg-gray-50 text-center">
                  操作
                </th>

                {/* 日付列: 常に表示 */}
                <th className="border border-gray-400 px-4 py-2 text-left font-normal whitespace-nowrap bg-gray-50">
                  日付
                </th>

                {/* 折りたたみ可能な列 */}
                {COLUMNS.map((col) => (
                  <CollapsibleTh
                    key={col.key}
                    label={col.label}
                    isExpanded={visibleCols[col.key]}
                    onToggle={() => toggleCol(col.key)}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {days.map((yyyymmdd) => {
                // DBデータがあれば対応行を取得、なければ null
                const row =
                  reports.find((r) => r.YYYYMMDD === yyyymmdd) ?? null;
                return (
                  <tr key={yyyymmdd} className="hover:bg-gray-50">
                    {/* 編集ボタン: 左端・データあり行のみ表示 */}
                    <td className="border border-gray-400 w-16 px-2 py-2 text-center whitespace-nowrap">
                      {row && (
                        <button
                          onClick={() => onNavigateToStore(yyyymmdd)}
                          disabled={isLocked(yyyymmdd, user?.shopNo, user?.regiNo)}
                          className={
                            isLocked(yyyymmdd, user?.shopNo, user?.regiNo)
                              ? "px-3 py-1 text-base font-medium bg-gray-300 text-gray-500 rounded shadow cursor-not-allowed"
                              : "px-3 py-1 text-base font-medium bg-gray-500 text-white rounded shadow active:shadow-none active:translate-y-px cursor-pointer hover:bg-gray-700"
                          }
                        >
                          編集
                        </button>
                      )}
                    </td>

                    {/* 日付 */}
                    <td className="border border-gray-400 px-4 py-2 whitespace-nowrap">
                      {formatDateDisplay(yyyymmdd)}
                    </td>

                    {/* 各データ列: データがなければ空白 */}
                    {COLUMNS.map((col) => {
                      const visible = visibleCols[col.key];
                      let displayValue;
                      let colorClass = "";
                      if (col.isText) {
                        displayValue = row ? (row.UpdatePerson ?? row.InputPerson ?? "") : "";
                      } else if (col.getValue) {
                        const v = col.getValue(row);
                        displayValue = col.signed ? formatSigned(v) : formatNumber(v);
                        // 現金相違額: 非ゼロのとき赤色表示（StoreInputFormと同じパターン）
                        if (col.key === "_kabusoku" && v) colorClass = "text-red-600";
                      } else {
                        displayValue = formatNumber(row?.[col.key]);
                      }
                      return (
                        <td
                          key={col.key}
                          className={`border border-gray-400 py-2 ${col.isText ? "text-left" : "text-right"} ${visible ? "px-3" : "w-6"} ${colorClass}`}
                        >
                          {visible && displayValue}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>

            {/* 合計行: データがある場合のみ表示 */}
            {reports.length > 0 && (
              <tfoot>
                <tr className="bg-gray-100 font-medium">
                  {/* 操作列・日付列は「合計」ラベル */}
                  <td className="border border-gray-400 w-16 px-2 py-2" />
                  <td className="border border-gray-400 px-4 py-2 whitespace-nowrap">合計</td>
                  {COLUMNS.map((col) => {
                    const visible = visibleCols[col.key];
                    if (col.isText) {
                      return (
                        <td key={col.key} className={`border border-gray-400 py-2 ${visible ? "px-3" : "w-6"}`} />
                      );
                    }
                    const total = reports.reduce((sum, row) => {
                      const v = col.getValue ? col.getValue(row) : toNum(row[col.key]);
                      return sum + toNum(v);
                    }, 0);
                    const displayTotal = col.signed ? formatSigned(total) : formatNumber(total);
                    // 現金相違額: 非ゼロのとき赤色表示（StoreInputFormと同じパターン）
                    const colorClass = col.key === "_kabusoku" && total ? "text-red-600" : "";
                    return (
                      <td
                        key={col.key}
                        className={`border border-gray-400 py-2 text-right ${visible ? "px-3" : "w-6"} ${colorClass}`}
                      >
                        {visible && displayTotal}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

export default MonthlyDataPage;
